import type { PriceCompareResponse, PriceTagProductCandidate, RpcErrorCode } from "../types";

/** Overlay states from spec §33-34, plus AUTHENTICATING/AUTH_ERROR for the pre-scan gate. */
export type ScannerState =
  | "AUTHENTICATING"
  | "AUTH_ERROR"
  | "CAMERA_READY"
  | "CAMERA_PERMISSION_DENIED"
  | "CAMERA_NOT_FOUND"
  | "SCANNING"
  | "DETECTED"
  | "LOOKUP"
  | "RESULT"
  | "NOT_FOUND"
  | "ERROR"
  | "OFFLINE"
  | "SESSION_EXPIRED"
  | "CONFIRM";

export interface ScannerModel {
  state: ScannerState;
  result: PriceCompareResponse | null;
  errorMessage: string | null;
  candidates?: PriceTagProductCandidate[] | null;
}

export type ScannerAction =
  | { type: "AUTH_FAILED"; message: string }
  | { type: "AUTH_SUCCEEDED" }
  | { type: "CAMERA_READY" }
  | { type: "CAMERA_ERROR"; message: string }
  | { type: "CAMERA_PERMISSION_DENIED" }
  | { type: "CAMERA_NOT_FOUND" }
  | { type: "RETRY_CAMERA" }
  | { type: "BARCODE_DETECTED" }
  | { type: "LOOKUP_START" }
  | { type: "LOOKUP_SUCCESS"; result: PriceCompareResponse }
  | { type: "LOOKUP_CANDIDATES"; candidates: PriceTagProductCandidate[] }
  | { type: "LOOKUP_FAILED"; code: RpcErrorCode | "TRANSPORT_CLOSED" }
  | { type: "GATEWAY_OFFLINE" }
  | { type: "GATEWAY_RECONNECTED" }
  | { type: "RESUME_SCANNING" };

export const initialScannerModel: ScannerModel = {
  state: "AUTHENTICATING",
  result: null,
  errorMessage: null,
  candidates: null,
};

const NOT_FOUND_CODES: ReadonlySet<RpcErrorCode | "TRANSPORT_CLOSED"> = new Set([
  "PRODUCT_NOT_FOUND",
  "INVALID_BARCODE",
  "NO_PRICE_DATA",
  "PRODUCT_NOT_MATCHED",
  "NO_TEXT_DETECTED",
]);

export function scannerReducer(model: ScannerModel, action: ScannerAction): ScannerModel {
  switch (action.type) {
    case "AUTH_FAILED":
      return { ...model, state: "AUTH_ERROR", errorMessage: action.message };
    case "AUTH_SUCCEEDED":
      return { ...model, state: "CAMERA_READY", errorMessage: null };
    case "CAMERA_READY":
      return { ...model, state: "SCANNING" };
    case "CAMERA_ERROR":
      return { ...model, state: "ERROR", errorMessage: action.message };
    case "CAMERA_PERMISSION_DENIED":
      return { ...model, state: "CAMERA_PERMISSION_DENIED", errorMessage: null };
    case "CAMERA_NOT_FOUND":
      return { ...model, state: "CAMERA_NOT_FOUND", errorMessage: null };
    case "RETRY_CAMERA":
      return { ...model, state: "SCANNING", errorMessage: null };
    case "BARCODE_DETECTED":
      if (model.state === "AUTHENTICATING" || model.state === "AUTH_ERROR" || model.state === "SESSION_EXPIRED") return model;
      return { ...model, state: "DETECTED" };
    case "LOOKUP_START":
      return { ...model, state: "LOOKUP", candidates: null };
    case "LOOKUP_SUCCESS":
      return { ...model, state: "RESULT", result: action.result, errorMessage: null, candidates: null };
    case "LOOKUP_CANDIDATES":
      return { ...model, state: "CONFIRM", candidates: action.candidates, result: null, errorMessage: null };
    case "LOOKUP_FAILED":
      if (action.code === "SESSION_EXPIRED") {
        return { ...model, state: "SESSION_EXPIRED", errorMessage: "Scan session has expired", candidates: null };
      }
      if (NOT_FOUND_CODES.has(action.code)) {
        return { ...model, state: "NOT_FOUND", result: null, candidates: null };
      }
      return { ...model, state: "ERROR", errorMessage: action.code, candidates: null };
    case "GATEWAY_OFFLINE":
      return { ...model, state: "OFFLINE" };
    case "GATEWAY_RECONNECTED":
      return { ...model, state: "SCANNING", errorMessage: null };
    case "RESUME_SCANNING":
      if (model.state === "SESSION_EXPIRED" || model.state === "AUTH_ERROR" || model.state === "OFFLINE" || model.state === "CAMERA_PERMISSION_DENIED" || model.state === "CAMERA_NOT_FOUND") return model;
      return { ...model, state: "SCANNING", result: null, candidates: null };
    default:
      return model;
  }
}

/** Scanning is only allowed once auth has succeeded and no terminal error/expiry has occurred (AC: invalid session blocks scanning). */
export function canScan(state: ScannerState): boolean {
  return state !== "AUTHENTICATING" && state !== "AUTH_ERROR" && state !== "SESSION_EXPIRED" && state !== "CAMERA_PERMISSION_DENIED" && state !== "CAMERA_NOT_FOUND";
}

/** Camera preview can start before WS auth finishes; lookups stay gated by canScan/handleDetected. */
export function canPreviewCamera(state: ScannerState): boolean {
  return (
    state !== "AUTH_ERROR" &&
    state !== "SESSION_EXPIRED" &&
    state !== "CAMERA_PERMISSION_DENIED" &&
    state !== "CAMERA_NOT_FOUND"
  );
}
